import React from 'react';
import Collapse, { Panel } from 'rc-collapse';
import { withTranslation, WithTranslation } from 'react-i18next';
import ContactLocalizationPanel from './ContactLocalizationContainer';
import LocalizationPanel, { LocalizationChangeHandler } from './LocalizationContainer';
import { TargetNode } from '../../models/TargetNode';


type ChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => void;

interface ContactFormPanelOwnProps {
  node: TargetNode;
  // Whether the optional contact fields are switched on (extra.c.c50).
  enabled: boolean;
  // The ids present in extra.k — a field is ticked iff its redisid is in here.
  extraKIds: Set<string>;
  onToggleEnabled: ChangeHandler;
  // Presence-toggles the field's redisid in extra.k.
  onChangeExtra: ChangeHandler;
  localizationChanged: LocalizationChangeHandler;
  // The contact-form title is a text field with its own handler and its own
  // localization panel (index 3 of the question array, not an extra.k entry).
  contactTitle: string;
  onChangeContactTitle: ChangeHandler;
  contactTitleLocalizationChanged: LocalizationChangeHandler;
}

interface FieldRow {
  label: string;   // i18n key
  name: string;    // form field name
  redisid: string; // key into extra.k
}

// The three collapsible groups, in render order. Each row is the same shape:
// a label, a checkbox toggling presence in extra.k, and a localization panel.
const CONTACT_ROWS: FieldRow[] = [
  { label: 'target.editor.contact.contactMe', name: 'contactFormPleaseContactMe', redisid: 'contactme' },
];

const CONTACT_ROWS_AFTER_TITLE: FieldRow[] = [
  { label: 'target.editor.contact.showName', name: 'contactFormShowName', redisid: 'name' },
  { label: 'target.editor.contact.showCompany', name: 'contactFormShowCompany', redisid: 'company' },
  { label: 'target.editor.contact.showEmail', name: 'contactFormShowEmail', redisid: 'email' },
  { label: 'target.editor.contact.showPhone', name: 'contactFormShowPhone', redisid: 'phone' },
  { label: 'target.editor.contact.showRoom', name: 'contactFormShowRoomNumber', redisid: 'room' },
  { label: 'target.editor.contact.showTeam', name: 'contactFormShowTeam', redisid: 'team' },
  { label: 'target.editor.contact.showAddress', name: 'contactFormShowStreetAddress', redisid: 'streetAddress' },
  { label: 'target.editor.contact.showZIP', name: 'contactFormShowPostalCode', redisid: 'postalCode' },
  { label: 'target.editor.contact.showCity', name: 'contactFormShowCity', redisid: 'city' },
  { label: 'target.editor.contact.showCountry', name: 'contactFormShowCountry', redisid: 'country' },
  { label: 'target.editor.contact.showAge', name: 'contactFormShowAge', redisid: 'age' },
  { label: 'target.editor.contact.showGenre', name: 'contactFormShowGender', redisid: 'gender' },
];

const CUSTOM_ROWS: FieldRow[] = [1, 2, 3, 4, 5, 6].map(n => ({
  label: `target.editor.custom.custom${n}`,
  name: `contactFormCustom${n}`,
  redisid: `custom${n}`,
}));

const MARKETING_ROWS: FieldRow[] = [
  { label: 'target.editor.marketing.noSMS', name: 'contactFormOkSendMarketingSMS', redisid: 'nomarketingsms' },
  { label: 'target.editor.marketing.noEmail', name: 'contactFormOkSendMarketingEmail', redisid: 'nomarketingemail' },
  { label: 'target.editor.marketing.noCall', name: 'contactFormOkSendMarketingCalls', redisid: 'nomarketingcall' },
  { label: 'target.editor.marketing.noText', name: 'contactFormNoMarketingMessages', redisid: 'nomarketingmessages' },
];

const ContactFormPanel = ({
  node, enabled, extraKIds, onToggleEnabled, onChangeExtra, localizationChanged,
  contactTitle, onChangeContactTitle, contactTitleLocalizationChanged, t,
}: ContactFormPanelOwnProps & WithTranslation) => {
  const addLocalisation = t('target.editor.button.addLocalisation');

  // One labelled checkbox plus its localization panel — written out 23 times in
  // the container before.
  const renderRow = ({ label, name, redisid }: FieldRow) => (
    <React.Fragment key={redisid}>
      <label>{t(label)}</label>
      <input type="checkbox" name={name} data-redisid={redisid}
        checked={extraKIds.has(redisid)}
        onChange={onChangeExtra} />
      <ContactLocalizationPanel
        node={node}
        localizationChanged={localizationChanged}
        header={addLocalisation}
        localizationIndex={redisid} />
    </React.Fragment>
  );

  return (
    <fieldset>
      <legend>{t('target.editor.label.optionalFields')}</legend>
      <label>{t('target.editor.label.enable')}</label>
      <input type="checkbox" data-redisid="showoptionalfields"
        title="Enable Optional Fields"
        checked={enabled}
        onChange={onToggleEnabled}
      />
      <div style={{ height: "0.2em" }}></div>
      <div className={!enabled ? "hidden" : ""}>
        <Collapse accordion={false}>
          <Panel header={t('target.editor.contact.title')} forceRender={true}>
            {CONTACT_ROWS.map(renderRow)}

            <label>{t('target.editor.contact.contactFormTitle')}</label>
            <input type="text" name="contactFormContactFormTitle"
              data-redisid="contactFormTitle"
              value={contactTitle}
              onChange={onChangeContactTitle} />
            <LocalizationPanel
              node={node}
              localizationChanged={contactTitleLocalizationChanged}
              header={addLocalisation}
              localizationIndex={3}
            />
            {CONTACT_ROWS_AFTER_TITLE.map(renderRow)}
          </Panel>
        </Collapse>
        <Collapse>
          <Panel header={t('target.editor.custom.title')} forceRender={true}>
            {CUSTOM_ROWS.map(renderRow)}
          </Panel>
        </Collapse>
        <Collapse>
          <Panel header={t('target.editor.marketing.title')} forceRender={true}>
            {MARKETING_ROWS.map(renderRow)}
          </Panel>
        </Collapse>
      </div>
    </fieldset>
  );
};

export default withTranslation('common')(ContactFormPanel);
